{
  "$comment": [
    "The EDITORIAL half of the module inventory — the part a person writes.",
    "",
    "It lived inside apps/console/src/data/modules.ts as a TypeScript const, which was",
    "fine while the Module Manager was its only reader. It is not any more: the",
    "marketplace publisher (`solum publish-catalog`, packages/cli/src/catalog.ts) writes",
    "the same label, category, summary and edition into controlplane.marketplace_module,",
    "and a vendor catalogue that disagrees with the customer's own Module Manager about",
    "what a module is CALLED is the two-sources-of-one-truth defect this repo keeps",
    "paying for (skill rule 18). So the data moved here, to plain JSON, and both readers",
    "read it — the console imports it, the Node CLI reads it off disk.",
    "",
    "It is EDITORIAL only. Facts — version, migration count, dependencies, kind,",
    "audience, sellable, schema — are never written here: they come from each module's",
    "module.json, computed at build time by scripts/gen-console-modules.mjs (MB-INFRA#239).",
    "",
    "`fallback` is the derivation for a module nobody has written copy for. It is DATA",
    "rather than a function for the same reason the rest of this file is: two readers",
    "with two copies of the rule is two rules. The lookup either side performs is",
    "mechanical; the decisions are all here."
  ],

  "editorial": {
    "core": { "label": "Directory & Governance", "group": "Platform", "edition": "Platform",
      "blurb": "Metamodel, party graph, MDM, authz, audit, events, licensing, rules — the kernel every module stands on." },

    "crm": { "label": "Sales (CRM)", "group": "Sales", "edition": "Pro",
      "blurb": "Accounts, contacts, opportunities — the party graph wearing sales roles." },
    "catalog": { "label": "Product Catalog", "group": "Sales", "edition": "Included",
      "blurb": "Products, price lists, volume-tier pricing — the shared commercial substrate." },
    "cpq": { "label": "CPQ", "group": "Sales", "edition": "Premium",
      "blurb": "Rule-driven configure-price-quote on the shared rule engine + catalog. Versioned quotes, multi-charge pricing, snapshot-on-fire rules, contracts, federation." },
    "tenders": { "label": "Bids & Tenders", "group": "Sales", "edition": "Pro",
      "blurb": "RFx pursuits qualify → bid/no-bid → submit → won/lost, with a questionnaire, dated tasks and a reusable answer library. Ties to CRM accounts/opportunities." },
    "partners": { "label": "Partners", "group": "Sales", "edition": "Pro",
      "blurb": "Partner accounts, referrals and margin — the indirect channel on the party graph." },

    "afd": { "label": "AFD Delivery", "group": "Delivery", "edition": "Pro",
      "blurb": "Projects (delivery / internal / support), the double-diamond flow, Confidence Gates, personas, health — the delivery methodology as a governed module." },
    "engagements": { "label": "Engagement Hub", "group": "Delivery", "edition": "Pro",
      "blurb": "Meetings, workshops, HTML decks and an EU-sovereign file exchange — decisions, acknowledgements, hashes and receipts." },

    "subscriptions": { "label": "Recurring & Seats", "group": "Recurring", "edition": "Pro",
      "blurb": "Plans, subscriptions and seat assignment. One deployment-wide human seat cap; new assignments at capacity are refused." },
    "billing": { "label": "Billing & Invoicing", "group": "Recurring", "edition": "Pro",
      "blurb": "VAT rates, gap-free numbering, issue-and-freeze invoices with immutable audited totals, credit notes & void." },
    "contracts": { "label": "Contracts", "group": "Recurring", "edition": "Pro",
      "blurb": "Agreements, a versioned clause library (snapshotted at sign), obligations. Data-driven lifecycle; effective-dated validity. Feeds subscriptions + billing." },

    "time": { "label": "Timesheets", "group": "Services", "edition": "Pro",
      "blurb": "Period timesheets (draft → submit → approve, SoD), a keyboard-first weekly grid, rate resolution & snapshot." },
    "funding": { "label": "Budgets & POs", "group": "Services", "edition": "Pro",
      "blurb": "Customer budgets / purchase orders + the drawdown ledger. Over-budget is a signal, never a lockout." },
    "service": { "label": "Customer Service", "group": "Services", "edition": "Pro",
      "blurb": "Cases, SLAs and KB on the party graph — data-driven case lifecycle; the crm-service bridge flags deals at risk." },

    "approvals": { "label": "Approvals & Workflow", "group": "Governance", "edition": "Premium",
      "blurb": "Process definitions, decision tables, SoD / 4-eyes / quorum, SLA timers, signals — the rule engine as a workflow product." },
    "kyc": { "label": "Compliance (KYC)", "group": "Governance", "edition": "Pro",
      "blurb": "Cases, risk scoring, document capture — sovereign compliance on the graph." },
    "compliance": { "label": "Compliance", "group": "Governance", "edition": "Pro",
      "blurb": "Compliance registers + evidence on the party graph — the NIS2 / ISO 27001 framing, data-driven." },
    "access": { "label": "Access & Identity", "group": "Governance", "edition": "Included",
      "blurb": "Org membership, SCIM provisioning and the tenancy floor every other module reads." },
    "documents": { "label": "Documents", "group": "Governance", "edition": "Included",
      "blurb": "Cross-module document generation — templates-as-data + a metamodel-driven renderer over any entity; SHA-256-sealed, append-only." },

    "portal": { "label": "Portal & Onboarding", "group": "Experience", "edition": "Pro",
      "blurb": "Per-user dashboards, personal settings, role-driven experience, onboarding flows." },
    "activity": { "label": "Activities", "group": "Experience", "edition": "Included",
      "blurb": "Tasks, notes and interactions — a common timeline any module can hang off." },
    "comms": { "label": "Comms Hub", "group": "Experience", "edition": "Pro",
      "blurb": "Multi-channel publishing — LinkedIn, newsletter (Listmonk) and website — with a scheduled publish sweep on the relay." },
    "feedback": { "label": "Feedback", "group": "Experience", "edition": "Pro",
      "blurb": "The Chrome-extension backend — captures, triage, the forge seam and the verify loop. Product + project feedback route separately." },

    "commerce": { "label": "Commerce", "group": "Commerce", "edition": "Pro",
      "blurb": "Cart · checkout · order · fulfilment (B2C, the Cocco lane). Data-driven order lifecycle; GMV." },

    "marketing": { "label": "Marketing Automation", "group": "Available", "edition": "Pro",
      "blurb": "Consented email comms to lists/segments + full interaction capture onto the graph. Designed, not yet built — it migrates nothing, so it cannot be installed." },

    "controlplane": { "label": "Control Plane", "group": "Internal", "edition": "Internal",
      "blurb": "Fleet + licensing — mints signed licenses, tracks deployments and heartbeats. Moonbase-only; fleet_operator-gated; never sold." },
    "controlplane-portal": { "label": "Control Plane ↔ Portal", "group": "Internal", "edition": "Internal",
      "blurb": "Moonbase-internal bridge — lets a customer administrator see their own deployments, set their update policy and fetch the artefacts their account has bought." }
  },

  "fallback": {
    "$comment": [
      "What a module with no editorial entry gets. Derived from the MANIFEST — kind,",
      "audience, sellable — never guessed, and never a commercial tier: an unsellable",
      "module is 'Included' and a bridge is 'Bridge' precisely so that nothing acquires",
      "a price by omission.",
      "",
      "`edition` is evaluated in this order: core → moonbaseInternal → bridge →",
      "sellable/unsellable. `group` likewise: bridge → moonbaseInternal → default."
    ],
    "edition": {
      "core": "Platform",
      "moonbaseInternal": "Internal",
      "bridge": "Bridge",
      "sellable": "Pro",
      "unsellable": "Included"
    },
    "group": {
      "bridge": "Bridges",
      "moonbaseInternal": "Internal",
      "default": "Modules"
    }
  }
}
